import { useEffect, useState } from "react";
import { Coins, ArrowUpRight, ArrowDownRight, Gift } from "lucide-react";
import { toast } from "sonner";
import { Rail } from "../components/Rail";
import api, { apiError, timeWIB } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { btnMint } from "../lib/ui";

export default function Billing() {
  const { account, refreshAccount } = useAuth();
  const [packages, setPackages] = useState([]);
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data));
    api.get("/billing/transactions").then((r) => setTxs(r.data));
    refreshAccount();
  }, [refreshAccount]);

  const topup = async (pkgId) => {
    setBusy(pkgId);
    try {
      await api.post("/payments/checkout", { package_id: pkgId });
      await refreshAccount();
      const r = await api.get("/billing/transactions");
      setTxs(r.data);
      toast.success("Token berhasil ditambahkan!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const icons = { bonus: Gift, topup: ArrowUpRight, usage: ArrowDownRight };

  return (
    <div className="flex h-screen bg-ink text-paper">
      <Rail />
      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 border-b border-white/5 bg-ink/80 px-8 py-5 backdrop-blur-md">
          <h1 className="text-xl font-semibold">Token</h1>
          <p className="text-sm text-muted">Top up token untuk menggunakan AI</p>
        </header>
        <div className="space-y-8 p-8">
          <div className="flex flex-wrap items-end gap-8 rounded-2xl border border-mint/20 bg-mint/5 p-6">
            <div>
              <p className="text-xs text-muted">Saldo Token</p>
              <p data-testid="token-balance" className="mt-1 font-mono text-4xl font-semibold text-mint">
                {(account?.token_balance || 0).toLocaleString("id-ID")}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted">Total Terpakai</p>
              <p data-testid="token-used" className="mt-1 font-mono text-2xl text-paper">{(account?.token_used || 0).toLocaleString("id-ID")}</p>
            </div>
          </div>

          <section>
            <h2 className="text-base font-semibold md:text-lg">Top Up Token</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {packages.map((p) => (
                <div key={p.id} className="rounded-2xl border border-white/5 bg-panel p-6 transition-colors hover:border-mint/30">
                  <Coins className="text-mint" size={20} />
                  <p className="mt-3 font-mono text-2xl font-semibold">{p.label}</p>
                  <button data-testid={`topup-btn-${p.id}`} disabled={busy === p.id} onClick={() => topup(p.id)} className={`${btnMint} mt-5 w-full`}>
                    {busy === p.id ? "Memproses…" : "Top Up Gratis"}
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold md:text-lg">Riwayat Transaksi</h2>
            <div className="mt-4 divide-y divide-white/5 rounded-2xl border border-white/5 bg-panel" data-testid="transactions-list">
              {txs.map((t) => {
                const Icon = icons[t.type] || Coins;
                return (
                  <div key={t.id} className="flex items-center gap-4 px-5 py-3.5">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${t.tokens >= 0 ? "bg-mint/10 text-mint" : "bg-white/5 text-faint"}`}>
                      <Icon size={15} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{t.description}</p>
                      <p className="font-mono text-[11px] text-faint">{new Date(t.created_at).toLocaleDateString("id-ID")} {timeWIB(t.created_at)} WIB</p>
                    </div>
                    <span className={`font-mono text-sm ${t.tokens >= 0 ? "text-mint" : "text-muted"}`}>
                      {t.tokens >= 0 ? "+" : ""}{t.tokens.toLocaleString("id-ID")}
                    </span>
                  </div>
                );
              })}
              {!txs.length && <p className="px-5 py-6 text-sm text-faint">Belum ada transaksi.</p>}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
